import { inject, Injectable } from '@angular/core';
import { type Schema } from '../../../../amplify/data/resource';
import { generateClient } from 'aws-amplify/api';
import { Task, TaskAuditEntry } from '../models/task.model';
import { ProjectLink } from '../models/project.model';
import { BehaviorSubject } from 'rxjs';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})

export class TaskService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private tasksSubject = new BehaviorSubject<Task[]>([]);
  tasks$ = this.tasksSubject.asObservable();

  /**
   * Non-null when tasks could not be loaded at all — a dropped subscription or a
   * schema mismatch between this client and the deployed API. Views must show this
   * instead of an "no tasks yet" empty state, which is indistinguishable otherwise.
   */
  private tasksErrorSubject = new BehaviorSubject<string | null>(null);
  tasksError$ = this.tasksErrorSubject.asObservable();

  private userService = inject(UserService);

  // Flag to pause refresh during editing
  private isRefreshPaused = false;

  constructor() {
    // Don't call data methods in constructor - they should be called after Amplify is configured
  }

  async init() {
    // Start real-time observers first
    this.startRealTimeObservers();

    // Initial load
    await this.listTask();
  }

  // Start real-time observers using Amplify's observeQuery
  startRealTimeObservers() {
    // Observe all tasks in real-time
    this.client.models.Task.observeQuery({
      filter: { isDeleted: { ne: true } },
      selectionSet: [
        'id',
        'nameTask',
        'content',
        'status',
        'priority',
        'startDate',
        'limitDateToDo',
        'order',
        'estimatedHours',
        'links',
        'createdAt',
        'owner',
        'employeeId',
        'projectStageId',
        'validatedBy',
        'validatedAt',
        'isDeleted'      ]
    }).subscribe({
      next: ({ items, isSynced }) => {
        const tasks: Task[] = items.map(t => ({
          id: t.id,
          nameTask: t.nameTask,
          content: t.content ?? undefined,
          status: t.status,
          priority: t.priority,
          startDate: t.startDate ?? undefined,
          limitDateToDo: t.limitDateToDo ?? undefined,
          order: t.order ?? 0,
          estimatedHours: t.estimatedHours,
          links: parseTaskLinks((t as any).links),
          createdAt: t.createdAt,
          owner: t.owner,
          employeeId: t.employeeId?.filter((id): id is string => id !== null) ?? [],
          projectStageId: t.projectStageId,
          validatedBy: (t as any).validatedBy ?? null,
          validatedAt: (t as any).validatedAt ?? null,
        })) as Task[];

        // Sort by order
        tasks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        this.tasksSubject.next(tasks);
        this.tasksErrorSubject.next(null);

        if (isSynced) {
          console.log('✅ [Tasks] Real-time sync completed');
        }
      },
      error: (error) => {
        console.error('❌ [Tasks] Real-time subscription error:', error);
        this.tasksErrorSubject.next(this.describeLoadFailure(error));
      }
    });
  }

  /** Returns the current tasks synchronously (snapshot of BehaviorSubject). */
  getTasksSnapshot(): Task[] {
    return this.tasksSubject.value;
  }

  // Pause and resume methods kept for backward compatibility (now no-ops)
  pauseRefresh() {
    this.isRefreshPaused = true;
  }

  resumeRefresh() {
    this.isRefreshPaused = false;
  }

  stopRealTimeRefresh() {
    // No-op with observeQuery - subscriptions managed automatically
  }

  // Observe tasks for a specific stage - utilizes the ordered index server-side
  observeTasksForStage(projectStageId: string) {
    return this.client.models.Task.observeQuery({
      filter: { projectStageId: { eq: projectStageId }, isDeleted: { ne: true } }
    });
  }

  async createTask(task: Task){
    try{
      // Get existing tasks for this stage to calculate next order
      const currentTasks = this.tasksSubject.value.filter(t => t.projectStageId === task.projectStageId);
      const nextOrder = task.order ?? currentTasks.length;

      const taskInput = {
        nameTask: task.nameTask,
        content: task.content || null,
        status: task.status || 'NOTBEGIN',
        priority: task.priority || 'LOW',
        startDate: task.startDate || null,
        limitDateToDo: task.limitDateToDo || null,
        order: nextOrder,
        employeeId: task.employeeId || [],
        projectStageId: task.projectStageId,
        links: task.links?.map(link => JSON.stringify(link)),
      };
      const {data, errors} = await this.client.models.Task.create(taskInput);
      if (errors && errors.length) {
        console.error('Errors creating task', errors);
        throw new Error(errors[0].message);
      }
      // No manual listTask() — observeQuery will pick up the new task automatically
      console.log('Task created', data);
      if (data?.id) {
        this.recordCreation(data.id).catch(e =>
          console.error('[TaskService] Failed to record task creation', e)
        );
      }
      return data;
    }catch(e){
      console.error('Failed when creating the task', e);
      throw e;
    }
  }

  /**
   * A completed task is frozen: the only change it accepts is the one that
   * reopens it (a lone `status` moving away from DONE).
   */
  private isReopen(updates: Partial<Task>): boolean {
    const keys = Object.keys(updates);
    return keys.length === 1 && keys[0] === 'status' && updates.status !== 'DONE';
  }

  /** True when the task exists and is DONE — callers use it to disable edit affordances. */
  isTaskLocked(taskId: string): boolean {
    return this.tasksSubject.value.find(t => t.id === taskId)?.status === 'DONE';
  }

  async updateTask(taskId: string, updates: Partial<Task>) {
    // Guard before the optimistic update so a rejected edit never reaches the UI.
    if (this.isTaskLocked(taskId) && !this.isReopen(updates)) {
      console.warn(`[TaskService] Task ${taskId} is DONE — reopen it before editing.`, updates);
      return null;
    }

    // Auto-set validation fields when status changes to DONE
    if (updates.status === 'DONE') {
      const currentUser = this.userService.getCurrentUserSnapshot();
      updates.validatedBy = currentUser?.id ?? null;
      updates.validatedAt = new Date().toISOString();
    } else if (updates.status) {
      // Clear validation when reverting from DONE
      updates.validatedBy = null;
      updates.validatedAt = null;
    }

    // Optimistic local update: immediately reflect in UI to prevent flicker/disappearance
    const currentTasks = this.tasksSubject.value;
    const previous = currentTasks.find(t => t.id === taskId);
    const taskIndex = currentTasks.findIndex(t => t.id === taskId);
    if (taskIndex !== -1) {
      const updatedTasks = [...currentTasks];
      updatedTasks[taskIndex] = { ...updatedTasks[taskIndex], ...updates };
      this.tasksSubject.next(updatedTasks);
    }

    try {
      // Amplify expects json arrays as strings — the local subject keeps the objects.
      const payload: any = { id: taskId, ...updates };
      if (updates.links !== undefined) {
        payload.links = updates.links.map(link => JSON.stringify(link));
      }
      const { data, errors } = await this.client.models.Task.update(payload);
      if (errors && errors.length) {
        console.error('GraphQL updateTask errors:', errors);
        // Revert optimistic update on error
        this.tasksSubject.next(currentTasks);
        throw errors;
      }
      // Trail is best-effort: a failed audit write must never fail the edit itself.
      this.recordAudit(taskId, previous, updates).catch(e =>
        console.error('[TaskService] Failed to write audit trail', e)
      );
      // No manual listTask() — observeQuery handles real-time sync
      return data;
    } catch (e) {
      console.error('Failed to update the task', e);
      // Revert optimistic update on error
      this.tasksSubject.next(currentTasks);
      return null;
    }
  }

  // ── Audit trail ─────────────────────────────────────────────────────────────

  /** Fields worth tracking, with the label shown in the history feed. */
  private static readonly AUDITED_FIELDS: Record<string, string> = {
    nameTask: 'Name',
    content: 'Description',
    status: 'Status',
    priority: 'Priority',
    startDate: 'Start date',
    limitDateToDo: 'Due date',
    estimatedHours: 'Estimated hours',
    employeeId: 'Assignees',
    projectStageId: 'Phase',
    links: 'Links',
  };

  /** Stable string form of a field value, so old/new can be compared and stored. */
  private serializeValue(field: string, value: any): string {
    if (value === null || value === undefined || value === '') return '';
    if (field === 'employeeId') return (value as string[]).filter(Boolean).join(',');
    if (field === 'links') return (value as any[]).map(l => l?.name ?? '').filter(Boolean).join(', ');
    if (field === 'startDate' || field === 'limitDateToDo') return String(value).slice(0, 10);
    return String(value);
  }

  /** Diff the saved task against the update and append one entry per changed field. */
  private async recordAudit(taskId: string, previous: Task | undefined, updates: Partial<Task>) {
    const changedBy = this.userService.getCurrentUserSnapshot()?.id;
    if (!changedBy) {
      console.warn(`[TaskService] No current user — change to task ${taskId} left no audit entry.`, updates);
      return;
    }
    const changedAt = new Date().toISOString();

    const writes = Object.keys(updates)
      .filter(field => field in TaskService.AUDITED_FIELDS)
      .map(field => ({
        field,
        oldValue: this.serializeValue(field, (previous as any)?.[field]),
        newValue: this.serializeValue(field, (updates as any)[field]),
      }))
      .filter(entry => entry.oldValue !== entry.newValue)
      .map(entry =>
        this.client.models.TaskAudit.create({
          taskId,
          field: entry.field,
          action: 'UPDATED' as const,
          oldValue: entry.oldValue,
          newValue: entry.newValue,
          changedBy,
          changedAt,
        })
      );

    await Promise.all(writes);
  }

  /** Record the creation of a task, so the history starts at the beginning. */
  private async recordCreation(taskId: string) {
    const changedBy = this.userService.getCurrentUserSnapshot()?.id;
    if (!changedBy) {
      console.warn(`[TaskService] No current user — creation of task ${taskId} left no audit entry.`);
      return;
    }
    await this.client.models.TaskAudit.create({
      taskId,
      field: 'task',
      action: 'CREATED' as const,
      changedBy,
      changedAt: new Date().toISOString(),
    });
  }

  /** Record a soft delete, which bypasses `updateTask` and so is not diffed. */
  private async recordDeletion(taskId: string, previous: Task | undefined) {
    const changedBy = this.userService.getCurrentUserSnapshot()?.id;
    if (!changedBy) {
      console.warn(`[TaskService] No current user — deletion of task ${taskId} left no audit entry.`);
      return;
    }
    await this.client.models.TaskAudit.create({
      taskId,
      field: 'task',
      action: 'DELETED' as const,
      oldValue: previous?.nameTask ?? '',
      changedBy,
      changedAt: new Date().toISOString(),
    });
  }

  /** Full change history of a task, oldest first. */
  async listAudits(taskId: string): Promise<TaskAuditEntry[]> {
    try {
      const entries: TaskAuditEntry[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await (this.client.models.TaskAudit.listTaskAuditsByTaskId as any)(
          { taskId },
          { sortDirection: 'ASC', limit: 200, nextToken }
        );
        if (result.errors?.length) console.error('[TaskService] listAudits errors', result.errors);
        entries.push(...((result.data || []) as TaskAuditEntry[]));
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return entries;
    } catch (e) {
      console.error('[TaskService] Failed to load audit trail', e);
      return [];
    }
  }

  async deleteTask(taskId: string){
    // Optimistic local removal
    const currentTasks = this.tasksSubject.value;
    const previous = currentTasks.find(t => t.id === taskId);
    this.tasksSubject.next(currentTasks.filter(t => t.id !== taskId));

    try{
      const { data, errors} = await this.client.models.Task.update({
        id: taskId,
        isDeleted: true,
        deletedAt: new Date().toISOString()
      });
      if (errors && errors.length) {
        console.error('Errors while soft-deleting task', errors);
        // Revert on error
        this.tasksSubject.next(currentTasks);
        return null;
      }
      // No manual listTask() — observeQuery handles removal
      console.log('Task soft-deleted', data);
      // Best-effort, like every other audit write: never fail the delete over it.
      this.recordDeletion(taskId, previous).catch(e =>
        console.error('[TaskService] Failed to record task deletion', e)
      );
      return data;
    }catch(e){
      console.error('Failed to soft-delete this task', e)
      // Revert on error
      this.tasksSubject.next(currentTasks);
      return null;
    }
  }

  async listTask() {
    try {
      const selectionSet = [
        'id',
        'nameTask',
        'content',
        'status',
        'priority',
        'startDate',
        'limitDateToDo',
        'order',
        'estimatedHours',
        'links',
        'createdAt',
        'owner',
        'employeeId',
        'projectStageId',
        'validatedBy',
        'validatedAt',
        'isDeleted'
      ];

      // Paginate through all results
      const allTasks: Task[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await (this.client.models.Task.list as any)({
          filter: { isDeleted: { ne: true } },
          selectionSet,
          limit: 200,
          nextToken,
        });
        if (result.errors?.length) {
          console.error("Error while getting Task", result.errors);
          this.tasksErrorSubject.next(this.describeLoadFailure(result.errors));
          return;
        }
        const page = (result.data || []).map((t: any) => ({
          id: t.id,
          nameTask: t.nameTask,
          content: t.content,
          status: t.status,
          priority: t.priority,
          startDate: t.startDate,
          limitDateToDo: t.limitDateToDo,
          order: t.order ?? 0,
          estimatedHours: t.estimatedHours,
          links: parseTaskLinks((t as any).links),
          createdAt: t.createdAt,
          owner: t.owner,
          employeeId: t.employeeId?.filter((id: any): id is string => id !== null) ?? [],
          projectStageId: t.projectStageId ?? '',
          validatedBy: t.validatedBy ?? null,
          validatedAt: t.validatedAt ?? null,
        }));
        allTasks.push(...page);
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);

      allTasks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      this.tasksSubject.next(allTasks);
      this.tasksErrorSubject.next(null);
    } catch (e) {
      console.error("Failed to get all tasks", e);
      this.tasksErrorSubject.next(this.describeLoadFailure(e));
    }
  }

  /**
   * Turn a GraphQL/network failure into something a user can act on. A field the
   * deployed API doesn't know about is by far the most likely cause after a schema
   * change that hasn't been deployed, and it is worth naming explicitly.
   */
  private describeLoadFailure(error: unknown): string {
    const text = JSON.stringify(
      error instanceof Error ? error.message : error,
      (_k, v) => (v instanceof Error ? v.message : v)
    ) ?? '';
    if (/Cannot query field|Unknown field|Validation error|FieldUndefined/i.test(text)) {
      return 'Tasks could not be loaded: this app expects fields the API does not have. The backend schema is probably not deployed.';
    }
    return 'Tasks could not be loaded. Check your connection and try again.';
  }

  /** Server-side filtered fetch — does NOT touch the shared BehaviorSubject.
   *  Use for dashboard / views that only need a subset. */
  async listTasksFiltered(options: { excludeDone?: boolean } = {}): Promise<Task[]> {
    try {
      const baseFilter: any = { isDeleted: { ne: true } };
      const filter = options.excludeDone
        ? { and: [baseFilter, { status: { ne: 'DONE' as const } }] }
        : baseFilter;

      const selectionSet = [
        'id',
        'nameTask',
        'content',
        'status',
        'priority',
        'startDate',
        'limitDateToDo',
        'order',
        'estimatedHours',
        'links',
        'createdAt',
        'owner',
        'employeeId',
        'projectStageId',
        'validatedBy',
        'validatedAt',
        'isDeleted'
      ];

      // Paginate through all results
      const allTasks: Task[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await (this.client.models.Task.list as any)({
          filter,
          selectionSet,
          limit: 200,
          nextToken,
        });
        if (result.errors?.length) console.error('[TaskService] listTasksFiltered errors', result.errors);
        const page = (result.data || []).map((t: any) => ({
          id: t.id,
          nameTask: t.nameTask,
          content: t.content,
          status: t.status,
          priority: t.priority,
          startDate: t.startDate,
          limitDateToDo: t.limitDateToDo,
          order: t.order ?? 0,
          estimatedHours: t.estimatedHours,
          links: parseTaskLinks((t as any).links),
          createdAt: t.createdAt,
          owner: t.owner,
          employeeId: t.employeeId?.filter((id: any): id is string => id !== null) ?? [],
          projectStageId: t.projectStageId ?? '',
          validatedBy: t.validatedBy ?? null,
          validatedAt: t.validatedAt ?? null,
        }));
        allTasks.push(...page);
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);

      return allTasks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as Task[];
    } catch (e) {
      console.error('[TaskService] listTasksFiltered failed', e);
      return [];
    }
  }

  async getTaskById(taskId: string) {
    const currentTasks = this.tasksSubject.value;
    const found = currentTasks.find(t => t.id === taskId);
    if (found) return found;
    try {
      const { data, errors } = await this.client.models.Task.get({ id: taskId });
      if (errors && errors.length) {
        console.error('Error fetching task by id', errors);
        return null;
      }
      if (!data) return null;
      const task: Task = {
        id: data.id,
        nameTask: data.nameTask,
        content: data.content,
        status: data.status,
        priority: data.priority,
        startDate: data.startDate,
        limitDateToDo: data.limitDateToDo,
        order: data.order ?? 0,
        estimatedHours: data.estimatedHours,
        createdAt: data.createdAt,
        owner: data.owner,
        employeeId: data.employeeId,
        projectStageId: data.projectStageId ?? '',
      };
      return task;
    } catch (e) {
      console.error('Failed to get task by id', e);
      return null;
    }
  }

  // REORDER METHODS
  /**
   * Positional write only. Deliberately not audited: moving one task shifts the
   * index of every task after it, so auditing `order` would bury real changes
   * under noise. Note it also bypasses `updateTask`, hence no DONE guard either —
   * a completed task may be displaced by its neighbours moving.
   */
  async reorderTasks(tasks: Task[]) {
    try {
      const updatePromises = tasks.map((task, index) =>
        this.client.models.Task.update({
          id: task.id!,
          order: index
        })
      );

      await Promise.all(updatePromises);
      await this.listTask();
      return true;
    } catch (e) {
      console.error('Failed to reorder tasks', e);
      return false;
    }
  }
}

/** Amplify stores json arrays as strings — turn them back into ProjectLink objects. */
function parseTaskLinks(links: any): ProjectLink[] | undefined {
  if (!links) return undefined;
  return links
    .filter((link: any) => link !== null)
    .map((link: any) => (typeof link === 'string' ? JSON.parse(link) : link));
}
